import {
  AlignLeft,
  Calendar,
  CheckSquare,
  Hash,
  Image,
  ListChecks,
  Mail,
  Medal,
  Star,
  User,
  BarChart3,
  Square,
  MessageSquareQuote,
  ThumbsUp,
  Gauge,
  ChevronDown,
  Link,
  SlidersHorizontal,
  PenTool,
  Phone,
  Grid3X3,
  Upload,
  GripVertical,
  MapPin,
  Scale,
  Video,
} from "lucide-react";
import { MdShortText } from "react-icons/md";

// Custom Image Left Icon Component
const ImageLeftIcon = ({ size = 30, className = "", ...props }) => {
  // Calculate height based on aspect ratio (30:22 = 1.3636:1)
  const height = Math.round(size / 1.3636);
  
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 30 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <rect x="0.5" y="0.5" width="29" height="21" rx="2.5" fill="white"/>
      <rect x="0.5" y="0.5" width="29" height="21" rx="2.5" stroke="currentColor" strokeWidth="0.5"/>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1 3C1 1.89543 1.89543 1 3 1H14C14.5523 1 15 1.44772 15 2V20C15 20.5523 14.5523 21 14 21H3C1.89543 21 1 20.1046 1 19V3ZM18 12.75C18 12.3358 18.3358 12 18.75 12H23.25C23.6642 12 24 12.3358 24 12.75C24 13.1642 23.6642 13.5 23.25 13.5H18.75C18.3358 13.5 18 13.1642 18 12.75Z"
        fill="currentColor"
      />
      <path
        d="M25.25 8.25C25.6642 8.25 26 8.58579 26 9C26 9.41421 25.6642 9.75 25.25 9.75H18.75C18.3358 9.75 18 9.41421 18 9C18 8.58579 18.3358 8.25 18.75 8.25H25.25Z"
        fill="currentColor"
      />
    </svg>
  );
};

// Custom Image Right Icon Component
const ImageRightIcon = ({ size = 30, className = "", ...props }) => {
  // Calculate height based on aspect ratio (30:22 = 1.3636:1)
  const height = Math.round(size / 1.3636);
  const clipId = `clip-right-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 30 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <g clipPath={`url(#${clipId})`}>
        <path d="M3 0.5H27C28.3807 0.5 29.5 1.61929 29.5 3V19C29.5 20.3807 28.3807 21.5 27 21.5H3C1.61929 21.5 0.5 20.3807 0.5 19V3C0.5 1.61929 1.61929 0.5 3 0.5Z" fill="white"/>
        <path d="M3 0.5H27C28.3807 0.5 29.5 1.61929 29.5 3V19C29.5 20.3807 28.3807 21.5 27 21.5H3C1.61929 21.5 0.5 20.3807 0.5 19V3C0.5 1.61929 1.61929 0.5 3 0.5Z" stroke="currentColor" strokeWidth="0.5"/>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M29 3C29 1.89543 28.1046 1 27 1H16C15.4477 1 15 1.44772 15 2V20C15 20.5523 15.4477 21 16 21H27C28.1046 21 29 20.1046 29 19V3ZM12 12.75C12 12.3358 11.6642 12 11.25 12H6.75C6.3358 12 6 12.3358 6 12.75C6 13.1642 6.3358 13.5 6.75 13.5H11.25C11.6642 13.5 12 13.1642 12 12.75Z"
          fill="currentColor"
        />
        <path
          d="M4.75 8.25C4.3358 8.25 4 8.58579 4 9C4 9.41421 4.3358 9.75 4.75 9.75H11.25C11.6642 9.75 12 9.41421 12 9C12 8.58579 11.6642 8.25 11.25 8.25H4.75Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="30" height="22" fill="white" transform="matrix(-1 0 0 1 30 0)"/>
        </clipPath>
      </defs>
    </svg>
  );
};

// Custom Image Cover Icon Component
const ImageCoverIcon = ({ size = 30, className = "", ...props }) => {
  // Calculate height based on aspect ratio (28:20 = 1.4:1)
  const height = Math.round(size / 1.4);
  const clipId = `clip-cover-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 28 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M26 0C27.1046 0 28 0.895431 28 2V18C28 19.1046 27.1046 20 26 20H2C0.895431 20 0 19.1046 0 18V2C0 0.895431 0.895431 0 2 0H26ZM6.75 10.75C6.3358 10.75 6 11.0858 6 11.5C6 11.9142 6.3358 12.25 6.75 12.25H18.25C18.6642 12.25 19 11.9142 19 11.5C19 11.0858 18.6642 10.75 18.25 10.75H6.75ZM6.75 7C6.3358 7 6 7.33579 6 7.75C6 8.16421 6.3358 8.5 6.75 8.5H20.25C20.6642 8.5 21 8.16421 21 7.75C21 7.33579 20.6642 7 20.25 7H6.75Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="28" height="20" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );
};

// Custom Image Top Icon Component (Mobile)
const ImageTopIcon = ({ size = 30, className = "", ...props }) => {
  // Calculate height based on aspect ratio (14:22 = 0.636:1)
  // For mobile icons, we want them taller, so we'll use a different calculation
  const height = Math.round(size * 1.571); // 22/14 = 1.571
  
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 14 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <rect x="0.5" y="0.5" width="13" height="21" rx="1.5" fill="white"/>
      <rect x="0.5" y="0.5" width="13" height="21" rx="1.5" stroke="currentColor" strokeWidth="0.5"/>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3 16.75C3 16.3358 3.33579 16 3.75 16H9C9.41421 16 9.75 16.3358 9.75 16.75V18.25C9.75 18.6642 9.41421 19 9 19H3.75C3.33579 19 3 18.6642 3 18.25V16.75ZM3.75 13C3.33579 13 3 13.3358 3 13.75V14.1642C3 14.5784 3.33579 14.9142 3.75 14.9142H10.25C10.6642 14.9142 11 14.5784 11 14.1642V13.75C11 13.3358 10.6642 13 10.25 13H3.75Z"
        fill="currentColor"
      />
      <rect x="13" y="10" width="12" height="9" rx="1" transform="rotate(180 13 10)" fill="currentColor"/>
    </svg>
  );
};

// Custom Image Bottom Icon Component (Mobile)
const ImageBottomIcon = ({ size = 30, className = "", ...props }) => {
  // Calculate height based on aspect ratio (14:22 = 0.636:1)
  const height = Math.round(size * 1.571); // 22/14 = 1.571
  const clipId = `clip-bottom-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 14 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <g clipPath={`url(#${clipId})`}>
        <path d="M12 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2V20C0.5 20.8284 1.17157 21.5 2 21.5H12C12.8284 21.5 13.5 20.8284 13.5 20V2C13.5 1.17157 12.8284 0.5 12 0.5Z" fill="white"/>
        <path d="M12 0.5H2C1.17157 0.5 0.5 1.17157 0.5 2V20C0.5 20.8284 1.17157 21.5 2 21.5H12C12.8284 21.5 13.5 20.8284 13.5 20V2C13.5 1.17157 12.8284 0.5 12 0.5Z" stroke="currentColor" strokeWidth="0.5"/>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M3 6.75C3 6.3358 3.33579 6 3.75 6H9C9.41421 6 9.75 6.3358 9.75 6.75V8.25C9.75 8.6642 9.41421 9 9 9H3.75C3.33579 9 3 8.6642 3 8.25V6.75ZM3.75 3C3.33579 3 3 3.3358 3 3.75V4.1642C3 4.5784 3.33579 4.9142 3.75 4.9142H10.25C10.6642 4.9142 11 4.5784 11 4.1642V3.75C11 3.3358 10.6642 3 10.25 3H3.75Z"
          fill="currentColor"
        />
        <path
          d="M2 12L12 12C12.5523 12 13 12.4477 13 13L13 20C13 20.5523 12.5523 21 12 21H2C1.44771 21 1 20.5523 1 20L1 13C1 12.4477 1.44771 12 2 12Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="14" height="22" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );
};

// Custom Image Cover Mobile Icon Component
const ImageCoverMobileIcon = ({ size = 30, className = "", ...props }) => {
  // Calculate height based on aspect ratio (14:22 = 0.636:1)
  const height = Math.round(size * 1.571); // 22/14 = 1.571
  const clipId = `clip-cover-mobile-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 14 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M13 21V1H1V21H13ZM3.75 9.5C3.3358 9.5 3 9.16421 3 8.75C3 8.33579 3.3358 8 3.75 8H10.25C10.6642 8 11 8.33579 11 8.75C11 9.16421 10.6642 9.5 10.25 9.5H3.75ZM3.75 12.25C3.3358 12.25 3 11.9142 3 11.5C3 11.0858 3.3358 10.75 3.75 10.75H8.25C8.6642 10.75 9 11.0858 9 11.5C9 11.9142 8.6642 12.25 8.25 12.25H3.75Z"
          fill="currentColor"
        />
      </g>
      <rect x="-0.5" y="0.5" width="13" height="21" rx="1.5" transform="matrix(1 0 0 -1 1 22)" stroke="currentColor" strokeWidth="0.5"/>
      <defs>
        <clipPath id={clipId}>
          <rect width="12" height="20" rx="1" transform="matrix(1 0 0 -1 1 21)" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );
};

// Custom Image Inline Icon Component (Desktop) - Image between question and options
const ImageInlineIcon = ({ size = 30, className = "", ...props }) => {
  const height = Math.round(size / 1.3636);
  
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 30 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <rect x="0.5" y="0.5" width="29" height="21" rx="1.5" fill="white"/>
      <rect x="0.5" y="0.5" width="29" height="21" rx="1.5" stroke="currentColor" strokeWidth="0.5"/>
      {/* Question text at top */}
      <rect x="6" y="2" width="18" height="2" rx="1" fill="currentColor"/>
      {/* Image in middle */}
      <rect x="8" y="6" width="14" height="8" rx="1" fill="currentColor"/>
      {/* Answer options at bottom */}
      <rect x="10" y="16" width="10" height="2" rx="1" fill="currentColor" opacity="0.6"/>
      <rect x="10" y="19" width="10" height="2" rx="1" fill="currentColor" opacity="0.6"/>
    </svg>
  );
};

// Custom Image Inline Icon Component (Mobile) - Image between question and options
const ImageInlineMobileIcon = ({ size = 30, className = "", ...props }) => {
  const height = Math.round(size * 1.571);
  
  return (
    <svg
      width={size}
      height={height}
      viewBox="0 0 14 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <rect x="0.5" y="0.5" width="13" height="21" rx="1.5" fill="white"/>
      <rect x="0.5" y="0.5" width="13" height="21" rx="1.5" stroke="currentColor" strokeWidth="0.5"/>
      {/* Question text at top */}
      <rect x="2" y="2" width="10" height="1.5" rx="0.75" fill="currentColor"/>
      {/* Image in middle */}
      <rect x="2" y="5" width="10" height="7" rx="1" fill="currentColor"/>
      {/* Answer options at bottom */}
      <rect x="3" y="14" width="8" height="2" rx="1" fill="currentColor" opacity="0.6"/>
      <rect x="3" y="17" width="8" height="2" rx="1" fill="currentColor" opacity="0.6"/>
    </svg>
  );
};

const inputTypes = {
  "short text": {
    label: "Short Text",
    icon: MdShortText,
    description: "Let respondents enter a brief answer in an open text field.",
    bgColor: "bg-[#FED7AA]",
    iconColor: "text-[#7C2D12]",
  },
  "multiple choice": {
    label: "Single Choice",
    icon: CheckSquare,
    description:
      "Allow respondents to select one answer from a predefined list.",
    bgColor: "bg-[#DBEAFE]",
    iconColor: "text-[#1E3A8A]",
  },
  "picture choice": {
    label: "Picture Choice",
    icon: Image,
    description:
      "Allow respondents to select one picture from a predefined pictures.",
    bgColor: "bg-[#DBEAFE]",
    iconColor: "text-[#1E3A8A]",
  },
  "long text": {
    label: "Long Text",
    icon: AlignLeft,
    description: "Gather more detailed responses to open-ended questions.",
    bgColor: "bg-[#FED7AA]",
    iconColor: "text-[#7C2D12]",
  },
  checkbox: {
    label: "Checkbox",
    icon: Square,
    description:
      "A single checkbox for yes/no or agreement questions.",
    bgColor: "bg-[#E9D5FF]",
    iconColor: "text-[#581C87]",
  },
  "checkbox list": {
    label: "Multiple Choice",
    icon: ListChecks,
    description:
      "Allow respondents to select multiple answers from a range of options.",
    bgColor: "bg-[#DBEAFE]",
    iconColor: "text-[#1E3A8A]",
  },
  numeric: {
    label: "Numeric",
    icon: Hash,
    description: "Collect precise numerical data from respondents.",
    bgColor: "bg-[#FED7AA]",
    iconColor: "text-[#7C2D12]",
  },
  "star rating": {
    label: "Star Rating",
    icon: Star,
    description:
      "Provide a quick way for respondents to give feedback on a star scale.",
    bgColor: "bg-[#FDE68A]",
    iconColor: "text-[#78350F]",
  },
  "opinion scale": {
    label: "Opinion Scale",
    icon: Medal,
    description:
      "Provide a quick way for respondents to give opinion on a scale of numbers.",
    bgColor: "bg-[#FDE68A]",
    iconColor: "text-[#78350F]",
  },
  date: {
    label: "Date",
    icon: Calendar,
    description: "Let respondents enter a date.",
    bgColor: "bg-[#FED7AA]",
    iconColor: "text-[#7C2D12]",
  },
  "contact info": {
    label: "Contact Info",
    icon: User,
    description: "Collect contact information with multiple fields like name, email, phone, and company.",
    bgColor: "bg-[#CCFBF1]",
    iconColor: "text-[#134E4A]",
  },
  email: {
    label: "Email",
    icon: Mail,
    description: "Collect email addresses from respondents.",
    bgColor: "bg-[#CCFBF1]",
    iconColor: "text-[#134E4A]",
  },
  "poll question": {
    label: "Poll Question",
    icon: BarChart3,
    description: "Allow respondents to vote on options and see real-time results after voting.",
    bgColor: "bg-[#E9D5FF]",
    iconColor: "text-[#581C87]",
  },
  "statement": {
    label: "Statement",
    icon: MessageSquareQuote,
    description: "Display a message or information with a Continue button. Great for personalized greetings using recall.",
    bgColor: "bg-[#FED7AA]",
    iconColor: "text-[#7C2D12]",
  },
  "yes no": {
    label: "Yes / No",
    icon: ThumbsUp,
    description: "A simple yes or no question for quick binary feedback.",
    bgColor: "bg-[#DBEAFE]",
    iconColor: "text-[#1E3A8A]",
  },
  "nps": {
    label: "NPS",
    icon: Gauge,
    description: "Net Promoter Score — measure loyalty on a 0–10 scale with detractor/promoter labels.",
    bgColor: "bg-[#FDE68A]",
    iconColor: "text-[#78350F]",
  },
  "dropdown": {
    label: "Dropdown",
    icon: ChevronDown,
    description: "Let respondents pick one option from a compact dropdown list. Great for long lists.",
    bgColor: "bg-[#DBEAFE]",
    iconColor: "text-[#1E3A8A]",
  },
  "url": {
    label: "URL",
    icon: Link,
    description: "Collect a website URL from respondents with link validation.",
    bgColor: "bg-[#CCFBF1]",
    iconColor: "text-[#134E4A]",
  },
  "slider": {
    label: "Slider",
    icon: SlidersHorizontal,
    description: "Let respondents pick a value by dragging a slider across a numeric range.",
    bgColor: "bg-[#FDE68A]",
    iconColor: "text-[#78350F]",
  },
  "signature": {
    label: "Signature",
    icon: PenTool,
    description: "Collect a handwritten signature on a drawing canvas.",
    bgColor: "bg-[#E2E8F0]",
    iconColor: "text-[#1E293B]",
  },
  "phone number": {
    label: "Phone Number",
    icon: Phone,
    description: "Collect a phone number with country code validation.",
    bgColor: "bg-[#CCFBF1]",
    iconColor: "text-[#134E4A]",
    pro: true,
  },
  "matrix": {
    label: "Matrix",
    icon: Grid3X3,
    description: "Rate multiple items on the same scale in a Likert-style grid.",
    bgColor: "bg-[#E9D5FF]",
    iconColor: "text-[#581C87]",
    pro: true,
  },
  "file upload": {
    label: "File Upload",
    icon: Upload,
    description: "Let respondents upload files, images, or documents.",
    bgColor: "bg-[#E2E8F0]",
    iconColor: "text-[#1E293B]",
    pro: true,
  },
  "ranking": {
    label: "Ranking",
    icon: GripVertical,
    description: "Let respondents drag and reorder items by priority.",
    bgColor: "bg-[#E9D5FF]",
    iconColor: "text-[#581C87]",
  },
  "address": {
    label: "Address",
    icon: MapPin,
    description: "Collect a structured address with street, city, and country.",
    bgColor: "bg-[#CCFBF1]",
    iconColor: "text-[#134E4A]",
  },
  "legal": {
    label: "Legal",
    icon: Scale,
    description: "Display terms and conditions with a required acceptance checkbox.",
    bgColor: "bg-[#E2E8F0]",
    iconColor: "text-[#1E293B]",
  },
  "video audio": {
    label: "Video / Audio",
    icon: Video,
    description: "Let respondents record a video or audio response.",
    bgColor: "bg-[#E2E8F0]",
    iconColor: "text-[#1E293B]",
    pro: true,
  },
};

// Single source of truth for how question types are grouped and colored —
// both the "Add Question" picker and the in-editor "Question Type" dropdown
// render from this so their categories/colors/order can't drift apart again.
export const questionTypeCategories = [
  {
    name: "Choice",
    types: ["multiple choice", "checkbox list", "picture choice", "yes no", "dropdown"],
    bgColor: "bg-[#DBEAFE]",
    iconColor: "text-[#1E3A8A]",
  },
  {
    name: "Open",
    types: ["short text", "long text", "statement", "numeric", "date"],
    bgColor: "bg-[#FED7AA]",
    iconColor: "text-[#7C2D12]",
  },
  {
    name: "Rating",
    types: ["star rating", "opinion scale", "nps", "slider"],
    bgColor: "bg-[#FDE68A]",
    iconColor: "text-[#78350F]",
  },
  {
    name: "Input",
    types: ["email", "url", "phone number", "address", "contact info"],
    bgColor: "bg-[#CCFBF1]",
    iconColor: "text-[#134E4A]",
  },
  {
    name: "Structured",
    types: ["matrix", "ranking", "checkbox", "poll question"],
    bgColor: "bg-[#E9D5FF]",
    iconColor: "text-[#581C87]",
  },
  {
    name: "Advanced",
    types: ["signature", "file upload", "legal", "video audio"],
    bgColor: "bg-[#E2E8F0]",
    iconColor: "text-[#1E293B]",
  },
  {
    name: "Screens",
    types: ["welcome", "ending"],
    bgColor: "bg-[#F3F4F6]",
    iconColor: "text-[#374151]",
  },
];

// Flat type order derived from the categories above (Choice, then Open, then
// Rating, ...), for consumers that just need a single ordered list rather
// than the grouped structure.
export const orderedQuestionTypes = questionTypeCategories.flatMap((c) => c.types);

export const PRO_QUESTION_TYPES = ["phone number", "matrix", "file upload", "video audio"];

export const isProOrHigher = (planType) =>
  ["pro", "growth", "enterprise1", "enterprise2", "enterprise3", "enterprise4"].includes(planType);

export const defaultOptions = {
  "short text": [],
  "multiple choice": ["Option 1", "Option 2"],
  "picture choice": [
    { img: null, label: "Option 1" }
  ],
  "long text": [],
  checkbox: ["Checkbox default description"],
  "checkbox list": ["Option 1", "Option 2"],
  numeric: [],
  "star rating": [],
  "opinion scale": [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  date: [],
  "contact info": [],
  email: [],
  "poll question": ["Option 1", "Option 2"],
  "statement": [],
  "yes no": ["Yes", "No"],
  "nps": [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  "dropdown": ["Option 1", "Option 2", "Option 3"],
  "url": [],
  "slider": [],
  "signature": [],
  "phone number": [],
  "matrix": [],
  "file upload": [],
  "ranking": ["Option 1", "Option 2", "Option 3"],
  "address": [],
  "legal": [],
  "video audio": [],
};

// Default contact info fields configuration
export const defaultContactFields = [
  {
    id: "firstName",
    label: "First Name",
    placeholder: "John",
    type: "text",
    required: true,
    enabled: true
  },
  {
    id: "lastName", 
    label: "Last Name",
    placeholder: "Doe",
    type: "text",
    required: true,
    enabled: true
  },
  {
    id: "email",
    label: "Email",
    placeholder: "john@example.com",
    type: "email",
    required: true,
    enabled: true
  },
  {
    id: "phone",
    label: "Phone Number",
    placeholder: "(201) 555-0123",
    type: "tel",
    required: false,
    enabled: true
  },
  {
    id: "company",
    label: "Company",
    placeholder: "Acme Inc",
    type: "text",
    required: false,
    enabled: true
  }
];

export const defaultAddressFields = [
  { id: "street", label: "Street Address", placeholder: "123 Main St", enabled: true },
  { id: "city", label: "City", placeholder: "New York", enabled: true },
  { id: "state", label: "State / Region", placeholder: "NY", enabled: true },
  { id: "zip", label: "Zip / Postal Code", placeholder: "10001", enabled: true },
  { id: "country", label: "Country", placeholder: "United States", enabled: true },
];

export const WelcomeIcon = (props) => {
  return <img src="/images/welcome-icon.svg" alt="Welcome" {...props} />;
};

export const EndingIcon = (props) => {
  return <img src="/images/ending-icon.svg" alt="Ending" {...props} />;
};

export const welcomeScreenInput = {
  label: "Welcome Screen",
  icon: WelcomeIcon,
  description: "Open with a warm intro or message to set the tone.",
  bgColor: "bg-gray-100",
  iconColor: "text-ink-muted",
  type: "welcome",
  buttonText: "Start",
};

export const endingScreenInput = {
  label: "Ending Screen",
  icon: EndingIcon,
  description: "Finish with a thank you, message, or next step.",
  bgColor: "bg-gray-100",
  iconColor: "text-ink-muted",
  type: "ending",
  buttonText: "Start",
};

export const defaultWelcomeScreen = {
  title: welcomeScreenInput.label,
  type: welcomeScreenInput.type,
  buttonText: welcomeScreenInput.buttonText,
  backgroundImage: null,
  backgroundAlignment: "right",
  backgroundAlignmentMobile: "top",
  embeds: [], // Array of video embed URLs
};

export const defaultEndingScreen = {
  title: "Thank you page",
  description: "",
  type: endingScreenInput.type,
  buttonText: endingScreenInput.buttonText, // Legacy - kept for backward compatibility
  buttons: [], // New buttons array - max 4 buttons
  backgroundImage: null,
  backgroundAlignment: "right",
  backgroundAlignmentMobile: "top",
  isDefaultEnding: false,
  scoreRange: {
    min: 0,
    max: 0,
  },
};

export const defaultBackgroundAligments = [
  {
    value: "left",
    Icon: ImageLeftIcon,
    label: "Image Left",
  },
  {
    value: "cover",
    Icon: ImageCoverIcon,
    label: "Full Cover",
  },
  {
    value: "right",
    Icon: ImageRightIcon,
    label: "Image Right",
  },
  {
    value: "inline",
    Icon: ImageInlineIcon,
    label: "Image Inline",
  },
];

export const defaultMobileBackgroundAligments = [
  {
    value: "top",
    Icon: ImageTopIcon,
    label: "Image Top",
  },
  {
    value: "cover",
    Icon: ImageCoverMobileIcon,
    label: "Full Cover",
  },
  {
    value: "bottom",
    Icon: ImageBottomIcon,
    label: "Image Bottom",
  },
  {
    value: "inline",
    Icon: ImageInlineMobileIcon,
    label: "Image Inline",
  },
];

export const operatorMethods = {
  stringEqualTo: (str1, str2) => {
    if (!str1 || !str2) {
      return false;
    }
    return str1.toString() === str2.toString();
  },

  stringNotEqualTo: (str1, str2) => {
    if (!str1 || !str2) {
      return false;
    }
    return str1.toString() !== str2.toString();
  },

  stringBeginWith: (str1, str2) => {
    if (!str1 || !str2) {
      return false;
    }
    return str1.toString().startsWith(str2.toString());
  },

  stringEndWith: (str1, str2) => {
    if (!str1 || !str2) {
      return false;
    }
    return str1.toString().endsWith(str2.toString());
  },

  stringContain: (str1, str2) => {
    if (!str1 || !str2) {
      return false;
    }
    return str1.toString().includes(str2.toString());
  },

  stringNotContain: (str1, str2) => {
    if (!str1 || !str2) {
      return false;
    }
    return !str1.toString().includes(str2.toString());
  },

  arrayContain: (arr, ele) => {
    if (!arr || !ele) {
      return false;
    }
    return Array.isArray(arr) && arr.includes(ele);
  },

  arrayNotContain: (arr, ele) => {
    if (!arr || !ele) {
      return false;
    }
    return Array.isArray(arr) && !arr.includes(ele);
  },

  numberEqualTo: (num1, num2) => {
    if (isNaN(num1) || isNaN(num2)) {
      return false;
    }
    return +num1 === +num2;
  },

  numberNotEqualTo: (num1, num2) => {
    if (isNaN(num1) || isNaN(num2)) {
      return false;
    }
    return +num1 !== +num2;
  },

  numberLowerThen: (num1, num2) => {
    if (isNaN(num1) || isNaN(num2)) {
      return false;
    }
    return +num1 < +num2;
  },

  numberGreaterThen: (num1, num2) => {
    if (isNaN(num1) || isNaN(num2)) {
      return false;
    }
    return +num1 > +num2;
  },

  numberLowerOrEqualTo: (num1, num2) => {
    if (isNaN(num1) || isNaN(num2)) {
      return false;
    }
    return +num1 <= +num2;
  },

  numberGreaterOrEqualTo: (num1, num2) => {
    if (isNaN(num1) || isNaN(num2)) {
      return false;
    }
    return +num1 >= +num2;
  },
  parseDate: (dateStr) => {
    if (!dateStr) {
      return null;
    }
    const [day, month, year] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day); // JS months are 0-indexed
  },

  dateEqualTo: (date1, date2) => {
    if (!date1 || !date2) {
      return false;
    }
    const d1 = operatorMethods.parseDate(date1);
    const d2 = operatorMethods.parseDate(date2);
    return d1.getTime() === d2.getTime();
  },

  dateNotEqualTo: (date1, date2) => {
    if (!date1 || !date2) {
      return false;
    }
    const d1 = operatorMethods.parseDate(date1);
    const d2 = operatorMethods.parseDate(date2);
    return d1.getTime() !== d2.getTime();
  },

  dateLowerThen: (date1, date2) => {
    if (!date1 || !date2) {
      return false;
    }
    const d1 = operatorMethods.parseDate(date1);
    const d2 = operatorMethods.parseDate(date2);
    return d1.getTime() < d2.getTime();
  },

  dateGreaterThen: (date1, date2) => {
    if (!date1 || !date2) {
      return false;
    }
    const d1 = operatorMethods.parseDate(date1);
    const d2 = operatorMethods.parseDate(date2);
    return d1.getTime() > d2.getTime();
  },

  dateLowerOrEqualTo: (date1, date2) => {
    if (!date1 || !date2) {
      return false;
    }
    const d1 = operatorMethods.parseDate(date1);
    const d2 = operatorMethods.parseDate(date2);
    return d1.getTime() <= d2.getTime();
  },

  dateGreaterOrEqualTo: (date1, date2) => {
    if (!date1 || !date2) {
      return false;
    }
    const d1 = operatorMethods.parseDate(date1);
    const d2 = operatorMethods.parseDate(date2);
    return d1.getTime() >= d2.getTime();
  },
};

export const questioTypeDefaultOperatorMap = {
  "short text": [
    { label: "is equal to", value: "stringEqualTo" },
    { label: "is not equal to", value: "stringNotEqualTo" },
    { label: "begins with", value: "stringBeginWith" },
    { label: "ends with", value: "stringEndWith" },
    { label: "contains", value: "stringContain" },
    { label: "do not contain", value: "stringNotContain" },
  ],
  "multiple choice": [
    { label: "is", value: "stringEqualTo" },
    { label: "is not", value: "stringNotEqualTo" },
  ],
  "picture choice": [
    { label: "is", value: "stringEqualTo" },
    { label: "is not", value: "stringNotEqualTo" },
  ],
  "long text": [
    { label: "is equal to", value: "stringEqualTo" },
    { label: "is not equal to", value: "stringNotEqualTo" },
    { label: "begins with", value: "stringBeginWith" },
    { label: "ends with", value: "stringEndWith" },
    { label: "contains", value: "stringContain" },
    { label: "do not contain", value: "stringNotContain" },
  ],
  "checkbox list": [
    { label: "contains", value: "arrayContain" },
    { label: "not contains", value: "arrayNotContain" },
  ],
  numeric: [
    { label: "is equal to", value: "numberEqualTo" },
    { label: "is not equal to", value: "numberNotEqualTo" },
    { label: "is lower than", value: "numberLowerThen" },
    { label: "is greater than", value: "numberGreaterThen" },
    { label: "is lower or equal to", value: "numberLowerOrEqualTo" },
    { label: "is greater or equal to", value: "numberGreaterOrEqualTo" },
  ],
  "star rating": [
    { label: "is equal to", value: "numberEqualTo" },
    { label: "is not equal to", value: "numberNotEqualTo" },
    { label: "is lower than", value: "numberLowerThen" },
    { label: "is greater than", value: "numberGreaterThen" },
    { label: "is lower or equal to", value: "numberLowerOrEqualTo" },
    { label: "is greater or equal to", value: "numberGreaterOrEqualTo" },
  ],
  "opinion scale": [
    { label: "is equal to", value: "numberEqualTo" },
    { label: "is not equal to", value: "numberNotEqualTo" },
    { label: "is lower than", value: "numberLowerThen" },
    { label: "is greater than", value: "numberGreaterThen" },
    { label: "is lower or equal to", value: "numberLowerOrEqualTo" },
    { label: "is greater or equal to", value: "numberGreaterOrEqualTo" },
  ],
  date: [
    { label: "is equal to", value: "dateEqualTo" },
    { label: "is not equal to", value: "dateNotEqualTo" },
    { label: "is lower than", value: "dateLowerThen" },
    { label: "is greater than", value: "dateGreaterThen" },
    { label: "is lower or equal to", value: "dateLowerOrEqualTo" },
    { label: "is greater or equal to", value: "dateGreaterOrEqualTo" },
  ],
  "contact info": [
    { label: "is equal to", value: "stringEqualTo" },
    { label: "is not equal to", value: "stringNotEqualTo" },
    { label: "contains", value: "stringContain" },
    { label: "do not contain", value: "stringNotContain" },
  ],
  "poll question": [
    { label: "is", value: "stringEqualTo" },
    { label: "is not", value: "stringNotEqualTo" },
  ],
};

export default inputTypes;
